import React, { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@cms/ui/components/card";
import { Button } from "@cms/ui/components/button";
import { Input } from "@cms/ui/components/input";
import { Label } from "@cms/ui/components/label";
import { Checkbox } from "@cms/ui/components/checkbox";
import { useApiQuery } from "@cms/portal-lib/hooks";
import { BookingModuleGuard, BookingModuleHeader } from "../components/booking-module-shell";
import { fetchBookingSettings, saveBookingSettings, type BookingSettings } from "../lib/booking";
import type { ClientOutletContext } from "../lib/outlet-context";

/** Booking rules: timezone, slot step, notice, horizon, auto-confirm. */
function SettingsCard({ api }: ClientOutletContext) {
  const query = useApiQuery(() => (api ? fetchBookingSettings(api) : null), [api]);
  const [settings, setSettings] = useState<BookingSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (query.data && settings === null) setSettings(query.data.settings);
  }, [query.data, settings]);

  const set = (patch: Partial<BookingSettings>) => {
    setSettings((current) => (current ? { ...current, ...patch } : current));
    setSaved(false);
  };

  const save = async () => {
    if (!api || !settings) return;
    setSaving(true);
    setError("");
    try {
      await saveBookingSettings(api, settings);
      setSaved(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between pb-2">
        <CardTitle className="text-base">Booking rules</CardTitle>
        <Button size="sm" onClick={() => { void save(); }} disabled={saving || !settings}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saved ? "Saved" : "Save rules"}
        </Button>
      </CardHeader>
      <CardContent>
        {error && <p className="mb-3 text-sm text-destructive">{error}</p>}
        {!settings && (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}
        {settings && (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="set-tz">Timezone</Label>
                <Input id="set-tz" value={settings.timezone}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ timezone: e.target.value })} />
                <p className="text-xs text-muted-foreground">IANA name, e.g. Europe/London.</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="set-step">Slot every (minutes)</Label>
                <Input id="set-step" type="number" min={5} max={240} value={settings.slot_step_minutes}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ slot_step_minutes: Number(e.target.value) })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="set-notice">Minimum notice (minutes)</Label>
                <Input id="set-notice" type="number" min={0} value={settings.min_notice_minutes}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ min_notice_minutes: Number(e.target.value) })} />
                <p className="text-xs text-muted-foreground">How close to a slot customers can still book.</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="set-advance">Book up to (days ahead)</Label>
                <Input id="set-advance" type="number" min={1} max={365} value={settings.max_advance_days}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ max_advance_days: Number(e.target.value) })} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={settings.auto_confirm === 1}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ auto_confirm: e.target.checked ? 1 : 0 })}
              />
              Confirm bookings automatically (untick to approve each one in the calendar first)
            </label>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function BookingSettingsPage() {
  return (
    <BookingModuleGuard title="Bookings" description="The rules booking slots follow.">
      {(ctx) => (
        <div>
          <BookingModuleHeader />
          <SettingsCard {...ctx} />
        </div>
      )}
    </BookingModuleGuard>
  );
}
